import React, { useEffect, useState, useRef, useContext } from "react";
import axios from "axios";
import { widget } from "../../Assets/charting_library/charting_library.min";
import * as Stomp from "stompjs";
import * as SockJS from "sockjs-client";
import { useSelector } from "react-redux";
import InfoPopUp from "../../Common/InfoPopUp";
import { FutureDataContent } from ".";
import { showToast } from "../../CoreDataService";
import InfoIcon from "@mui/icons-material/Info";
import FutureChartSlotHeader from "./MultiChart/FutureChartSlotHeader";
import { useTradingColors } from "../Spot/tradingColorConfig";

const title = "Chart Widget";
const description =
  "Displays trade chart for 1 minute, 3 minutes, 5 minutes, 15 minutes, 30 minutes, 1 hour, 4 hours, 1 day, 2 days, 3 days, 1 week, 3 weeks, 1 month, 6 months, and years as well.";

const getTradingOverrides = (up, down) => ({
  "mainSeriesProperties.candleStyle.upColor": up,
  "mainSeriesProperties.candleStyle.downColor": down,
  "mainSeriesProperties.candleStyle.drawWick": true,
  "mainSeriesProperties.candleStyle.drawBorder": true,
  "mainSeriesProperties.candleStyle.borderColor": up,
  "mainSeriesProperties.candleStyle.borderUpColor": up,
  "mainSeriesProperties.candleStyle.borderDownColor": down,
  "mainSeriesProperties.candleStyle.wickUpColor": up,
  "mainSeriesProperties.candleStyle.wickDownColor": down,
  "mainSeriesProperties.barStyle.upColor": up,
  "mainSeriesProperties.barStyle.downColor": down,
  "mainSeriesProperties.hollowCandleStyle.upColor": up,
  "mainSeriesProperties.hollowCandleStyle.downColor": down,
  "mainSeriesProperties.hollowCandleStyle.borderColor": up,
  "mainSeriesProperties.hollowCandleStyle.borderUpColor": up,
  "mainSeriesProperties.hollowCandleStyle.borderDownColor": down,
  "mainSeriesProperties.hollowCandleStyle.wickUpColor": up,
  "mainSeriesProperties.hollowCandleStyle.wickDownColor": down,
  "mainSeriesProperties.haStyle.upColor": up,
  "mainSeriesProperties.haStyle.downColor": down,
  "mainSeriesProperties.haStyle.borderColor": up,
  "mainSeriesProperties.haStyle.borderUpColor": up,
  "mainSeriesProperties.haStyle.borderDownColor": down,
  "mainSeriesProperties.haStyle.wickUpColor": up,
  "mainSeriesProperties.haStyle.wickDownColor": down,
  "volume.volume.color.0": down,
  "volume.volume.color.1": up,
});

const getVolumeOverrides = (up, down) => ({
  "volume.volume.color.0": down,
  "volume.volume.color.1": up,
  "volume.volumePalette.0.color": down,
  "volume.volumePalette.1.color": up,
  "volume.volumePalette.colors.0.color": down,
  "volume.volumePalette.colors.1.color": up,
  "volume.Volume.color.0": down,
  "volume.Volume.color.1": up,
});

const updateVolumeStudyColors = (widgetInstance, up, down) => {
  if (!widgetInstance) return;

  try {
    if (typeof widgetInstance.chart === "function") {
      const chart = widgetInstance.chart();
      if (chart && typeof chart.getAllStudies === "function") {
        const allStudies = chart.getAllStudies() || [];
        allStudies.forEach((studyInfo) => {
          if (
            studyInfo &&
            studyInfo.name &&
            studyInfo.name.toLowerCase().includes("volume")
          ) {
            try {
              const studyApi = chart.getStudyById(studyInfo.id);
              if (studyApi && typeof studyApi.applyOverrides === "function") {
                studyApi.applyOverrides({
                  "Volume.color.0": down,
                  "Volume.color.1": up,
                  "Volume.volumePalette.0.color": down,
                  "Volume.volumePalette.1.color": up,
                  "Volume.volumePalette.colors.0.color": down,
                  "Volume.volumePalette.colors.1.color": up,
                  "vol.color.0": down,
                  "vol.color.1": up,
                  "volume.color.0": down,
                  "volume.color.1": up,
                  "palettes.volumePalette.colors.0.color": down,
                  "palettes.volumePalette.colors.1.color": up,
                });
              }

              const rawStudy = studyApi?._study;
              if (rawStudy && typeof rawStudy.properties === "function") {
                const props = rawStudy.properties();
                const palColors = props?.palettes?.volumePalette?.colors;
                if (palColors) {
                  if (palColors[0]?.color?.setValue) palColors[0].color.setValue(down);
                  if (palColors[1]?.color?.setValue) palColors[1].color.setValue(up);
                }
                if (typeof rawStudy.updateAllViews === "function") {
                  rawStudy.updateAllViews();
                }
              }
            } catch (studyErr) {
              console.warn("[Futures TVChart] Error updating study:", studyErr);
            }
          }
        });
      }
    }
  } catch (e) {
    console.warn("[Futures TVChart] Error updating chart studies:", e);
  }
};

const applyChartColors = (widgetInstance, up, down) => {
  if (!widgetInstance) return;
  try {
    if (typeof widgetInstance.applyOverrides === "function") {
      widgetInstance.applyOverrides(getTradingOverrides(up, down));
    }
    if (typeof widgetInstance.applyStudiesOverrides === "function") {
      widgetInstance.applyStudiesOverrides(getVolumeOverrides(up, down));
    }
    updateVolumeStudyColors(widgetInstance, up, down);
    setTimeout(() => {
      updateVolumeStudyColors(widgetInstance, up, down);
    }, 150);
  } catch (err) {
    console.warn("[Futures TVChart] Error applying color overrides:", err);
  }
};

const FutureTVChart = ({
  currencycode: propCurrencyCode,
  basecurrency: propBaseCurrency,
  assetpair: propAssetPair,
  assetPairName: propAssetPairName,
  instanceId = "main",
  height = "402px",
  containerId: propContainerId,
  showHeader = false,
  isActive = false,
  btnColor,
  onActivate,
  onSymbolChange,
  onClose,
  showClose = false,
}) => {
  const contextData = useContext(FutureDataContent) || {};
  const currencycode = (propCurrencyCode || contextData.currencycode || "BTC").toUpperCase();
  const basecurrency = (propBaseCurrency || contextData.basecurrency || "USDT").toUpperCase();
  const assetpair = (propAssetPair || contextData.assetpair || `${currencycode}${basecurrency}_PERP`).toUpperCase();
  const assetPairName = propAssetPairName || contextData.assetPairName || `${currencycode}${basecurrency} Perpetual`;

  const logo = localStorage.getItem("exchangeLogo");
  const [show, setShow] = useState(false);
  const { upColor, downColor } = useTradingColors();

  // Colors reference for callbacks
  const colorsRef = useRef({ upColor, downColor });
  const isChartReadyRef = useRef(false);

  useEffect(() => {
    colorsRef.current = { upColor, downColor };
  }, [upColor, downColor]);

  // Interval per instance
  const intervalStorageKey = `tv_interval_futures_${instanceId}`;
  const [interval, setInterval] = useState(
    localStorage.getItem(intervalStorageKey) || "60"
  );

  const [datafeedUrl] = useState(
    "https://accounts.paybito.com/ChartApi/futures"
  );
  const [libraryPath] = useState("/charting_library/");
  const [clientId] = useState("tradingview.com");
  const [userId] = useState(localStorage.getItem("uuid"));
  const [fullscreen] = useState(false);
  const [autosize] = useState(true);
  const containerId = propContainerId || `tv_futures_chart_${instanceId}`;
  const [tvWidget, setTvWidget] = useState(null);
  const [Themecolor] = useState(
    localStorage.getItem("theme") || "dark"
  );
  const [isLoading, setLoading] = useState(false);

  const handleClose = () => setShow(false);

  const websocketRef = useRef(null);
  const realtimeCallbackRef = useRef(null);
  const lastBarRef = useRef(null);

  const webSocketEndPoint =
    "https://futures-stream.paybito.com/FutureChartStream/ws";
  const topic = "/user/topic/stream/tradingView";

  const reqNoRef = useRef(null);
  const intervalRef = useRef(interval);

  /* ---------------- SAVE / LOAD LAYOUT ---------------- */

  const saveChartState = (widgetInstance) => {
    if (!widgetInstance) return;
    const chartKey = `tvChartDerivative_${userId}_${instanceId}_${assetpair}`;
    widgetInstance.save((state) => {
      localStorage.setItem(chartKey, JSON.stringify(state));
      localStorage.setItem(`tvChartDerivative_${userId}_lastSaved`, chartKey);
      showToast("success", "Futures chart layout saved!");
    });
  };

  const loadChartState = (widgetInstance, callback) => {
    if (!widgetInstance) return;
    const chartKey = `tvChartDerivative_${userId}_${instanceId}_${assetpair}`;
    const savedState = localStorage.getItem(chartKey);

    if (savedState) {
      try {
        widgetInstance.load(JSON.parse(savedState));
        setTimeout(() => {
          applyChartColors(widgetInstance, colorsRef.current.upColor, colorsRef.current.downColor);
        }, 120);
        if (callback) callback();
      } catch (err) {
        console.error("Error loading saved chart state:", err);
        if (callback) callback();
      }
    } else if (callback) {
      callback();
    }
  };

  /* ---------------- WEBSOCKET ---------------- */

  const connectWebSocket = () => {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) return;

    try {
      const ws = new SockJS(webSocketEndPoint);
      const stompClient = Stomp.over(ws);
      stompClient.debug = null;
      websocketRef.current = stompClient;

      stompClient.connect(
        {},
        () => {
          stompClient.subscribe(topic, onMessageReceived);
          subscribe();
        },
        (error) => {
          console.warn("Futures WebSocket Error:", error);
          if (localStorage.getItem("access_token")) {
            setTimeout(connectWebSocket, 5000);
          }
        }
      );
    } catch (e) {
      console.warn("WebSocket connect error:", e);
    }
  };

  const disconnectWebSocket = () => {
    if (websocketRef.current?.connected) {
      websocketRef.current.disconnect();
    }
  };

  const sendMessage = (message) => {
    const client = websocketRef.current;
    if (client && client.connected) {
      const sendEndpoint =
        currencycode.toLowerCase() === "btc" && basecurrency.toLowerCase() === "usd"
          ? "/app/sendRequest/BTCUSD"
          : "/app/sendRequest/tradingView";

      client.send(sendEndpoint, {}, JSON.stringify(message));
    }
  };

  const mapResolution = (res) => {
    const resolutionMap = {
      "1": "1MIN", "3": "3MIN", "5": "5MIN", "15": "15MIN", "30": "30MIN",
      "60": "1HR", "240": "4HR",
    };
    return resolutionMap[res] || res;
  };

  const subscribe = (resolution) => {
    const res = resolution || intervalRef.current;
    reqNoRef.current = Math.floor(Math.random() * 1000) + 1;
    const req = {
      method: "SUBSCRIBE",
      base: basecurrency,
      counter: currencycode,
      contract: assetpair,
      actualResolution: mapResolution(res),
      id: reqNoRef.current,
    };
    sendMessage(req);
    localStorage.setItem("isUnsubscribeOccured", "false");
  };

  const unsubscribe = () => {
    if (reqNoRef.current != null) {
      const req = {
        method: "UNSUBSCRIBE",
        base: basecurrency,
        counter: currencycode,
        contract: assetpair,
        actualResolution: mapResolution(intervalRef.current),
        id: reqNoRef.current,
      };
      sendMessage(req);
      localStorage.setItem("isUnsubscribeOccured", "true");
    }
  };

  const onMessageReceived = (message) => {
    try {
      const parsedMessage = JSON.parse(message.body);
      if (parsedMessage && parsedMessage.t && parsedMessage.o && parsedMessage.c) {
        updateRealtimeBar(parsedMessage);
      }
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
    }
  };

  const updateRealtimeBar = (data) => {
    if (!realtimeCallbackRef.current) return;

    try {
      const bar = {
        time: Number(data.t[0]) * 1000,
        open: Number(data.o[0]),
        high: Number(data.h[0]),
        low: Number(data.l[0]),
        close: Number(data.c[0]),
        volume: Number(data.v[0]) * 0.7,
      };

      if (lastBarRef.current && bar.time === lastBarRef.current.time) {
        bar.high = Math.max(bar.high, lastBarRef.current.high);
        bar.low = Math.min(bar.low, lastBarRef.current.low);
        bar.open = lastBarRef.current.open;
      }

      lastBarRef.current = bar;
      realtimeCallbackRef.current(bar);
    } catch (error) {
      console.error("Error updating realtime bar:", error);
    }
  };

  /* ---------------- DATAFEED ---------------- */

  const createDatafeed = () => ({
    onReady: (cb) =>
      setTimeout(
        () =>
          cb({
            supported_resolutions: [
              "1",
              "3",
              "5",
              "15",
              "30",
              "60",
              "240",
              "1D",
              "2D",
              "3D",
              "1W",
              "3W",
              "1M",
              "6M",
            ],
          }),
        0
      ),

    resolveSymbol: async (
      symbolName,
      onSymbolResolvedCallback,
      onResolveErrorCallback
    ) => {
      const split_data = symbolName.split(/[:/]/);
      const symbol_stub = {
        name: symbolName,
        description: `${split_data[1]?.toUpperCase() || assetpair}`,
        type: "crypto",
        session: "24x7",
        timezone: "Etc/UTC",
        ticker: symbolName,
        exchange: split_data[0] || "PAYBITO",
        minmov: 1,
        pricescale: 100,
        has_intraday: true,
        intraday_multipliers: ["1", "60"],
        supported_resolutions: [
          "1",
          "3",
          "5",
          "15",
          "30",
          "60",
          "240",
          "1D",
          "2D",
          "3D",
          "1W",
          "3W",
          "1M",
          "6M",
        ],
        volume_precision: 8,
        full_name: symbolName,
        listed_exchange: "PAYBITO",
      };
      setTimeout(() => onSymbolResolvedCallback(symbol_stub), 0);
    },

    getBars: async (
      symbolInfo,
      resolution,
      from,
      to,
      onHistoryCallback,
      onErrorCallback
    ) => {
      try {
        const response = await axios.get(
          `${datafeedUrl}/history?symbol=${assetpair}&resolution=${resolution}&from=${from}&to=${to}`
        );

        if (response.data && response.data.c && response.data.c.length > 0) {
          const bars = response.data.c.map((c, i) => ({
            close: Number(c),
            high: Number(response.data.h[i]),
            low: Number(response.data.l[i]),
            open: Number(response.data.o[i]),
            time: Number(response.data.t[i]) * 1000,
            volume: Number(response.data.v[i]) * 0.7,
          }));

          bars.sort((a, b) => a.time - b.time);

          if (bars.length > 0) {
            lastBarRef.current = bars[bars.length - 1];
          }

          onHistoryCallback(bars, { noData: false });
        } else {
          onHistoryCallback([], { noData: true });
        }
      } catch (error) {
        console.error("Error fetching bars:", error);
        onHistoryCallback([], { noData: true });
      }
    },

    subscribeBars: (symbolInfo, resolution, onRealtimeCallback) => {
      realtimeCallbackRef.current = onRealtimeCallback;
    },

    unsubscribeBars: () => {
      realtimeCallbackRef.current = null;
      lastBarRef.current = null;
    },

    calculateHistoryDepth: (resolution) => {
      if (resolution === "1D" || resolution === "3D") {
        return { resolutionBack: "M", intervalBack: 6 };
      }
      return parseInt(resolution) < 60
        ? { resolutionBack: "D", intervalBack: 1 }
        : undefined;
    },
  });

  /* ---------------- INIT CHART ---------------- */

  useEffect(() => {
    setLoading(true);

    realtimeCallbackRef.current = null;
    lastBarRef.current = null;

    if (tvWidget) {
      tvWidget.remove();
    }

    const widgetOptions = {
      symbol: `:${assetpair}`,
      datafeed: createDatafeed(),
      interval: interval,
      container_id: containerId,
      library_path: libraryPath,
      locale: "en",
      disabled_features: [
        "header_compare",
        "header_symbol_search",
        "widget_logo",
      ],
      enabled_features: [
        "hide_left_toolbar_by_default",
        "header_indicators",
        "header_chart_type",
        "save_chart_properties_to_local_storage",
        "create_volume_indicator_by_default",
        "use_localstorage_for_settings",
        "header_saveload",
      ],
      client_id: clientId,
      user_id: userId,
      fullscreen,
      autosize,
      theme: Themecolor,
      overrides: getTradingOverrides(upColor, downColor),
      studies_overrides: getVolumeOverrides(upColor, downColor),
    };

    const newTvWidget = new widget(widgetOptions);

    newTvWidget.onChartReady(() => {
      isChartReadyRef.current = true;

      loadChartState(newTvWidget);

      const { upColor: curUp, downColor: curDown } = colorsRef.current;
      applyChartColors(newTvWidget, curUp, curDown);

      setTimeout(() => {
        applyChartColors(newTvWidget, colorsRef.current.upColor, colorsRef.current.downColor);
      }, 150);

      newTvWidget.headerReady().then(() => {
        const saveButton = newTvWidget.createButton();
        saveButton.textContent = "Save Layout";
        saveButton.addEventListener("click", () => {
          saveChartState(newTvWidget);
        });
      });

      newTvWidget
        .chart()
        .onIntervalChanged()
        .subscribe(null, (newInterval) => {
          unsubscribe();
          intervalRef.current = newInterval;
          localStorage.setItem(intervalStorageKey, newInterval);
          setInterval(newInterval);
          subscribe(newInterval);
        });

      setLoading(false);
    });

    setTvWidget(newTvWidget);
    disconnectWebSocket();
    connectWebSocket();

    return () => {
      isChartReadyRef.current = false;
      realtimeCallbackRef.current = null;
      lastBarRef.current = null;
      newTvWidget.remove();
      disconnectWebSocket();
    };
  }, [currencycode, basecurrency, assetpair]);

  useEffect(() => {
    if (tvWidget && isChartReadyRef.current) {
      applyChartColors(tvWidget, upColor, downColor);
    }
  }, [upColor, downColor, tvWidget]);

  return (
    <div
      style={{
        position: "relative",
        height: height || "100%",
        width: "100%",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      onClick={onActivate}
    >
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background:
              Themecolor === "light" ? "rgb(250, 250, 250)" : "#1e2329",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "5px",
          }}
        >
          <div className="img text-center">
            {logo && <img className="loadImg" src={logo} height={45} alt="broker_logo" />}
            <h3 style={{ fontSize: "14px", color: "#fff" }} className="mt-3">
              Loading...
            </h3>
          </div>
        </div>
      )}

      {showHeader && (
        <FutureChartSlotHeader
          currencycode={currencycode}
          basecurrency={basecurrency}
          assetpair={assetpair}
          assetPairName={assetPairName}
          interval={interval}
          onIntervalSelect={(newInt) => {
            if (tvWidget && tvWidget.chart) {
              try {
                tvWidget.chart().setResolution(newInt);
              } catch (e) {
                console.error("setResolution error", e);
              }
            }
          }}
          onSymbolSelect={(newAsset) => {
            if (onSymbolChange) {
              onSymbolChange(newAsset);
            }
          }}
          isActive={isActive}
          btnColor={btnColor}
          onActivate={onActivate}
          onClose={onClose}
          showClose={showClose}
        />
      )}

      <div
        id={containerId}
        style={{
          flex: 1,
          width: "100%",
          height: showHeader ? `calc(100% - 32px)` : "100%",
          minHeight: 0,
          background: Themecolor === "dark" ? "#1e2329" : "rgb(250, 250, 250)",
          borderRadius: "5px",
        }}
      />

      <InfoPopUp
        open={show}
        onClose={handleClose}
        title={title}
        description={description}
      />
    </div>
  );
};

export default FutureTVChart;
